import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Loader2, Check, Sparkles, Play, Video, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: "Avatar", description: "Upload avatar image (shared across scenes)" },
  { id: 2, title: "Product", description: "Upload sample product image" },
  // Scene 1
  { id: 3, title: "S1 Background", description: "Scene 1 — Upload background image" },
  { id: 4, title: "S1 Composite", description: "Scene 1 — Generate composite image" },
  { id: 5, title: "S1 Script", description: "Scene 1 — Action prompt & script" },
  { id: 6, title: "S1 Video", description: "Scene 1 — Generate video" },
  // Scene 2
  { id: 7, title: "S2 Background", description: "Scene 2 — Upload background image" },
  { id: 8, title: "S2 Composite", description: "Scene 2 — Generate composite image" },
  { id: 9, title: "S2 Script", description: "Scene 2 — Action prompt & script" },
  { id: 10, title: "S2 Video", description: "Scene 2 — Generate video" },
  // Finish
  { id: 11, title: "Stitch", description: "Combine scene videos" },
  { id: 12, title: "Publish", description: "Details & publish" },
];

export default function UGC2SceneCreator({ template, setTemplate }) {
  const sceneCount = 2;
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Shared state
  const [sceneBackgrounds, setSceneBackgrounds] = useState(template?.scene_backgrounds || []);
  const [sceneCompositePrompts, setSceneCompositePrompts] = useState(template?.scene_composite_prompts || ["", ""]);
  const [sceneCompositeImages, setSceneCompositeImages] = useState(template?.scene_composite_images || []);
  const [sceneScripts, setSceneScripts] = useState(template?.scene_scripts || ["", ""]);
  const [sceneActionPrompts, setSceneActionPrompts] = useState(template?.scene_action_prompts || ["", ""]);
  const [sceneVideoPrompts, setSceneVideoPrompts] = useState(template?.scene_video_prompts || []);
  const [sceneVideos, setSceneVideos] = useState(template?.scene_videos || []);

  // Methods
  const [compositeMethod, setCompositeMethod] = useState(template?.composite_method || "gemini");
  const [videoMethod, setVideoMethod] = useState(template?.video_method || "fal_veo");

  // Stitch
  const [stitchStatus, setStitchStatus] = useState(null);

  // Publish fields
  const [title, setTitle] = useState(template?.title || "");
  const [description, setDescription] = useState(template?.description || "");
  const [creditsCost, setCreditsCost] = useState(template?.credits_cost != null ? String(template.credits_cost) : "");
  const [tags, setTags] = useState(template?.tags || []);
  const [newTag, setNewTag] = useState("");

  useEffect(() => {
    if (template) {
      setSceneBackgrounds(template.scene_backgrounds || []);
      setSceneCompositeImages(template.scene_composite_images || []);
      setSceneCompositePrompts(template.scene_composite_prompts || ["", ""]);
      setSceneScripts(template.scene_scripts || ["", ""]);
      setSceneActionPrompts(template.scene_action_prompts || ["", ""]);
      setSceneVideoPrompts(template.scene_video_prompts || []);
      setSceneVideos(template.scene_videos || []);
      if (template.title) setTitle(template.title);
      if (template.description) setDescription(template.description);
      if (template.credits_cost != null) setCreditsCost(String(template.credits_cost));
      if (template.tags?.length) setTags(template.tags);
      if (template.composite_method) setCompositeMethod(template.composite_method);
      if (template.video_method) setVideoMethod(template.video_method);
    }
  }, [template]);

  // ─── Shared upload helpers ──────────────────────────────
  const uploadAvatar = async (file) => {
    if (!template) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await axios.post(`${API}/api/templates/${template.id}/upload-avatar`, formData, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      setTemplate({ ...template, avatar_image: resp.data.avatar_image });
      toast.success("Avatar uploaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const uploadProduct = async (file) => {
    if (!template) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await axios.post(`${API}/api/templates/${template.id}/upload-sample-product`, formData, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      setTemplate({ ...template, sample_product_image: resp.data.sample_product_image });
      toast.success("Product image uploaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const uploadSceneBackground = async (index, file) => {
    if (!template) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("scene_index", index);
      const resp = await axios.post(`${API}/api/templates/${template.id}/upload-scene-background`, formData, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      const updated = [...sceneBackgrounds];
      while (updated.length <= index) updated.push(null);
      updated[index] = resp.data.background_url;
      setSceneBackgrounds(updated);
      setTemplate({ ...template, scene_backgrounds: updated });
      toast.success(`Scene ${index + 1} background uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  // ─── Composite generation ──────────────────────────────
  const generateSceneComposite = async (index) => {
    if (!template) return;
    const prompt = sceneCompositePrompts[index];
    if (!prompt?.trim()) {
      toast.error("Enter a composite prompt");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("scene_index", index);
      formData.append("prompt", prompt);
      formData.append("method", compositeMethod);
      const resp = await axios.post(`${API}/api/templates/${template.id}/generate-ugc-scene-composite`, formData, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      const jobId = resp.data.job_id;
      toast.info(`Scene ${index + 1} composite generation started...`);
      const pollInterval = setInterval(async () => {
        try {
          const jobResp = await axios.get(`${API}/api/templates/${template.id}/composite-job/${jobId}`, { withCredentials: true });
          if (jobResp.data.status === "completed") {
            clearInterval(pollInterval);
            const tResp = await axios.get(`${API}/api/templates/${template.id}`, { withCredentials: true });
            const t = tResp.data.template || tResp.data;
            setTemplate(t);
            setSceneCompositeImages(t.scene_composite_images || []);
            setLoading(false);
            toast.success(`Scene ${index + 1} composite generated`);
          } else if (jobResp.data.status === "failed") {
            clearInterval(pollInterval);
            setLoading(false);
            toast.error(jobResp.data.message || "Generation failed");
          }
        } catch {
          clearInterval(pollInterval);
          setLoading(false);
        }
      }, 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generation failed");
      setLoading(false);
    }
  };

  // ─── Script save (per scene) ──────────────────────────
  const saveSceneScript = async (index) => {
    if (!template) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/templates/${template.id}/save-scene-scripts`, {
        scripts: sceneScripts,
        action_prompts: sceneActionPrompts,
      }, { withCredentials: true });
      if (sceneActionPrompts[index] && sceneScripts[index]) {
        const updated = [...sceneVideoPrompts];
        updated[index] = `${sceneActionPrompts[index]}\n\nScript: "${sceneScripts[index]}"`;
        setSceneVideoPrompts(updated);
      }
      toast.success(`Scene ${index + 1} script saved`);
    } catch (error) {
      toast.error("Failed to save script");
    } finally {
      setLoading(false);
    }
  };

  // ─── Video generation (per scene) ─────────────────────
  const generateSceneVideo = async (index) => {
    if (!template) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("scene_index", index);
      formData.append("video_method", videoMethod);
      const resp = await axios.post(`${API}/api/templates/${template.id}/generate-scene-video`, formData, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.info(`Scene ${index + 1} video generation started`);
      const jobId = resp.data.job_id;
      const interval = setInterval(async () => {
        try {
          const jobResp = await axios.get(`${API}/api/templates/${template.id}/scene-video-job/${jobId}`, { withCredentials: true });
          if (jobResp.data.status === "completed") {
            clearInterval(interval);
            const tResp = await axios.get(`${API}/api/templates/${template.id}`, { withCredentials: true });
            const t = tResp.data.template || tResp.data;
            setTemplate(t);
            setSceneVideos(t.scene_videos || []);
            setLoading(false);
            toast.success(`Scene ${index + 1} video ready`);
          } else if (jobResp.data.status === "failed") {
            clearInterval(interval);
            setLoading(false);
            toast.error(`Scene ${index + 1} video failed: ${jobResp.data.message}`);
          }
        } catch {
          clearInterval(interval);
          setLoading(false);
        }
      }, 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start video generation");
      setLoading(false);
    }
  };

  // ─── Stitch ────────────────────────────────────────────
  const stitchVideos = async () => {
    if (!template) return;
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/api/templates/${template.id}/stitch-preview`, {}, { withCredentials: true });
      toast.info("Stitching videos...");
      const jobId = resp.data.job_id;
      const interval = setInterval(async () => {
        try {
          const jobResp = await axios.get(`${API}/api/templates/${template.id}/stitch-job/${jobId}`, { withCredentials: true });
          setStitchStatus(jobResp.data);
          if (jobResp.data.status === "completed") {
            clearInterval(interval);
            const tResp = await axios.get(`${API}/api/templates/${template.id}`, { withCredentials: true });
            const t = tResp.data.template || tResp.data;
            setTemplate(t);
            setLoading(false);
            toast.success("Videos stitched!");
          } else if (jobResp.data.status === "failed") {
            clearInterval(interval);
            setLoading(false);
            toast.error("Stitch failed: " + jobResp.data.message);
          }
        } catch {
          clearInterval(interval);
          setLoading(false);
        }
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Stitch failed");
      setLoading(false);
    }
  };

  // ─── Publish ───────────────────────────────────────────
  const publishTemplate = async () => {
    if (!template || !title.trim() || !description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    setLoading(true);
    try {
      if (creditsCost) {
        await axios.put(`${API}/api/templates/${template.id}`, { credits_cost: parseFloat(creditsCost) }, { withCredentials: true });
      }
      await axios.post(`${API}/api/templates/${template.id}/publish`, { title, description, tags }, { withCredentials: true });
      toast.success("Template published!");
      setTemplate({ ...template, status: "published", title, description, tags, credits_cost: creditsCost ? parseFloat(creditsCost) : null });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Publish failed");
    } finally {
      setLoading(false);
    }
  };

  // ─── Reusable UI blocks ────────────────────────────────
  const ImageUploadZone = ({ id, onUpload, existing, label }) => (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{label}</p>
      {existing ? (
        <div>
          <img src={existing} alt="Uploaded" className="max-h-48 rounded-lg" />
          <Button variant="outline" size="sm" className="mt-2" onClick={() => document.getElementById(id).click()}>
            Replace
          </Button>
        </div>
      ) : (
        <div
          className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => document.getElementById(id).click()}
          data-testid={id}
        >
          <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Click to upload</p>
        </div>
      )}
      <input id={id} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} />
    </div>
  );

  const MethodSelector = ({ value, onChange, type }) => {
    const options = type === "composite"
      ? [
          { key: "nano_banana", title: "Fal AI (Nano Banana)", desc: "Seed-based reproducibility" },
          { key: "gemini", title: "Gemini 3 Pro Preview", desc: "Advanced text overlay support" },
        ]
      : [
          { key: "fal_veo", title: "Fal AI (Veo Wrapper)", desc: "Current workflow" },
          { key: "gemini_veo", title: "Gemini Veo 3.1 Fast", desc: "Direct Google API" },
          { key: "fal_flf", title: "Veo 3.1 Fast — FLF", desc: "Loopable video" },
          { key: "fal_sora2", title: "Sora 2", desc: "OpenAI Sora 2 via Fal.ai" },
        ];
    return (
      <div className="grid grid-cols-2 gap-3">
        {options.map((o) => (
          <div
            key={o.key}
            onClick={() => onChange(o.key)}
            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${value === o.key ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
            data-testid={`ugc2-${type}-${o.key}`}
          >
            <div className="font-medium text-sm">{o.title}</div>
            <div className="text-xs text-muted-foreground mt-1">{o.desc}</div>
          </div>
        ))}
      </div>
    );
  };

  // ─── Step rendering ────────────────────────────────────
  const renderStep = () => {
    switch (currentStep) {
      // Step 1: Avatar (shared)
      case 1:
        return (
          <ImageUploadZone
            id="ugc2-avatar-upload"
            onUpload={uploadAvatar}
            existing={template?.avatar_image}
            label="Upload an avatar image. This will be used for both scenes."
          />
        );

      // Step 2: Product (shared)
      case 2:
        return (
          <ImageUploadZone
            id="ugc2-product-upload"
            onUpload={uploadProduct}
            existing={template?.sample_product_image}
            label="Upload a sample product image."
          />
        );

      // Step 3: Scene 1 Background
      case 3:
        return (
          <ImageUploadZone
            id="ugc2-s1-bg-upload"
            onUpload={(file) => uploadSceneBackground(0, file)}
            existing={sceneBackgrounds[0]}
            label="Upload background image for Scene 1."
          />
        );

      // Step 4: Scene 1 Composite
      case 4:
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Composite Method</Label>
            <p className="text-xs text-muted-foreground mb-2">Used for all scenes and recreations.</p>
            <MethodSelector value={compositeMethod} onChange={setCompositeMethod} type="composite" />

            <div className="border rounded-lg p-4 space-y-3 mt-4">
              <Label className="text-sm font-medium">Scene 1 Composite Prompt</Label>
              <Textarea
                value={sceneCompositePrompts[0] || ""}
                onChange={(e) => {
                  const arr = [...sceneCompositePrompts];
                  arr[0] = e.target.value;
                  setSceneCompositePrompts(arr);
                }}
                placeholder="Describe how to combine avatar, product, and background for Scene 1..."
                className="min-h-[80px]"
                data-testid="s1-composite-prompt"
              />
              <Button onClick={() => generateSceneComposite(0)} disabled={loading} size="sm" data-testid="gen-s1-composite">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                Generate Scene 1 Composite
              </Button>
              {sceneCompositeImages[0] && (
                <img src={sceneCompositeImages[0]} alt="Scene 1 Composite" className="max-h-48 rounded-lg mt-2" />
              )}
            </div>
          </div>
        );

      // Step 5: Scene 1 Script
      case 5:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Write the action prompt and script for Scene 1. The action describes movement/behavior, the script is what the avatar says.
            </p>
            <div>
              <Label className="text-sm font-medium">Action Prompt</Label>
              <p className="text-xs text-muted-foreground mb-1">Static action description. Reused for all recreations.</p>
              <Textarea
                value={sceneActionPrompts[0] || ""}
                onChange={(e) => {
                  const arr = [...sceneActionPrompts];
                  arr[0] = e.target.value;
                  setSceneActionPrompts(arr);
                }}
                placeholder="e.g., Make the person speak naturally with subtle head movements..."
                className="min-h-[70px]"
                data-testid="s1-action-prompt"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Script</Label>
              <Textarea
                value={sceneScripts[0] || ""}
                onChange={(e) => {
                  const arr = [...sceneScripts];
                  arr[0] = e.target.value;
                  setSceneScripts(arr);
                }}
                placeholder="Script for Scene 1..."
                className="min-h-[70px]"
                data-testid="s1-script"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {(sceneScripts[0] || "").trim().split(/\s+/).filter(w => w).length}/20 words
              </p>
            </div>
            <Button
              onClick={() => saveSceneScript(0)}
              disabled={loading || !sceneScripts[0]?.trim() || !sceneActionPrompts[0]?.trim()}
              className="w-full"
              data-testid="save-s1-script"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
              Save Scene 1 Script
            </Button>
          </div>
        );

      // Step 6: Scene 1 Video
      case 6:
        return (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Video Generation Method</Label>
            <p className="text-xs text-muted-foreground mb-2">Used for all scenes and recreations.</p>
            <MethodSelector value={videoMethod} onChange={setVideoMethod} type="video" />

            <div className="border rounded-lg p-4 mt-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Scene 1 Video</Label>
                {sceneVideos[0] && <Badge variant="secondary"><Check className="w-3 h-3 mr-1" />Done</Badge>}
              </div>
              {sceneVideos[0] ? (
                <div>
                  <video src={sceneVideos[0]} controls className="w-full max-h-48 rounded-lg" />
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => generateSceneVideo(0)}>
                    Regenerate
                  </Button>
                </div>
              ) : (
                <Button onClick={() => generateSceneVideo(0)} disabled={loading || !sceneCompositeImages[0]} className="w-full" data-testid="gen-s1-video">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                  Generate Scene 1 Video
                </Button>
              )}
            </div>
          </div>
        );

      // Step 7: Scene 2 Background
      case 7:
        return (
          <ImageUploadZone
            id="ugc2-s2-bg-upload"
            onUpload={(file) => uploadSceneBackground(1, file)}
            existing={sceneBackgrounds[1]}
            label="Upload background image for Scene 2."
          />
        );

      // Step 8: Scene 2 Composite
      case 8:
        return (
          <div className="space-y-4">
            <div className="border rounded-lg p-4 space-y-3">
              <Label className="text-sm font-medium">Scene 2 Composite Prompt</Label>
              <Textarea
                value={sceneCompositePrompts[1] || ""}
                onChange={(e) => {
                  const arr = [...sceneCompositePrompts];
                  arr[1] = e.target.value;
                  setSceneCompositePrompts(arr);
                }}
                placeholder="Describe how to combine avatar, product, and background for Scene 2..."
                className="min-h-[80px]"
                data-testid="s2-composite-prompt"
              />
              <Button onClick={() => generateSceneComposite(1)} disabled={loading} size="sm" data-testid="gen-s2-composite">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                Generate Scene 2 Composite
              </Button>
              {sceneCompositeImages[1] && (
                <img src={sceneCompositeImages[1]} alt="Scene 2 Composite" className="max-h-48 rounded-lg mt-2" />
              )}
            </div>
          </div>
        );

      // Step 9: Scene 2 Script
      case 9:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Write the action prompt and script for Scene 2.
            </p>
            <div>
              <Label className="text-sm font-medium">Action Prompt</Label>
              <p className="text-xs text-muted-foreground mb-1">Static action description. Reused for all recreations.</p>
              <Textarea
                value={sceneActionPrompts[1] || ""}
                onChange={(e) => {
                  const arr = [...sceneActionPrompts];
                  arr[1] = e.target.value;
                  setSceneActionPrompts(arr);
                }}
                placeholder="e.g., Make the person speak naturally with subtle head movements..."
                className="min-h-[70px]"
                data-testid="s2-action-prompt"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Script</Label>
              <Textarea
                value={sceneScripts[1] || ""}
                onChange={(e) => {
                  const arr = [...sceneScripts];
                  arr[1] = e.target.value;
                  setSceneScripts(arr);
                }}
                placeholder="Script for Scene 2..."
                className="min-h-[70px]"
                data-testid="s2-script"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {(sceneScripts[1] || "").trim().split(/\s+/).filter(w => w).length}/20 words
              </p>
            </div>
            <Button
              onClick={() => saveSceneScript(1)}
              disabled={loading || !sceneScripts[1]?.trim() || !sceneActionPrompts[1]?.trim()}
              className="w-full"
              data-testid="save-s2-script"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
              Save Scene 2 Script
            </Button>
          </div>
        );

      // Step 10: Scene 2 Video
      case 10:
        return (
          <div className="space-y-4">
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Scene 2 Video</Label>
                {sceneVideos[1] && <Badge variant="secondary"><Check className="w-3 h-3 mr-1" />Done</Badge>}
              </div>
              {sceneVideos[1] ? (
                <div>
                  <video src={sceneVideos[1]} controls className="w-full max-h-48 rounded-lg" />
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => generateSceneVideo(1)}>
                    Regenerate
                  </Button>
                </div>
              ) : (
                <Button onClick={() => generateSceneVideo(1)} disabled={loading || !sceneCompositeImages[1]} className="w-full" data-testid="gen-s2-video">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                  Generate Scene 2 Video
                </Button>
              )}
            </div>
          </div>
        );

      // Step 11: Stitch
      case 11: {
        const allVideosReady = sceneVideos[0] && sceneVideos[1];
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Combine both scene videos into a single preview.</p>

            {/* Scene previews */}
            <div className="grid grid-cols-2 gap-4">
              {[0, 1].map((i) => (
                <div key={i} className="border rounded-lg p-3">
                  <Label className="text-xs font-medium mb-1 block">Scene {i + 1}</Label>
                  {sceneVideos[i] ? (
                    <video src={sceneVideos[i]} controls className="w-full rounded" />
                  ) : (
                    <div className="aspect-video bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                      No video
                    </div>
                  )}
                </div>
              ))}
            </div>

            {template?.sample_video ? (
              <div>
                <Label className="text-sm font-medium">Stitched Preview</Label>
                <video src={template.sample_video} controls className="w-full max-h-64 rounded-lg mt-2" />
                <Button variant="outline" onClick={stitchVideos} disabled={loading} className="mt-2">Re-stitch</Button>
              </div>
            ) : (
              <Button onClick={stitchVideos} disabled={loading || !allVideosReady} className="w-full" data-testid="stitch-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Video className="w-4 h-4 mr-1" />}
                Stitch Videos
              </Button>
            )}
            {stitchStatus?.status === "processing" && (
              <p className="text-sm text-muted-foreground">{stitchStatus.message}</p>
            )}
          </div>
        );
      }

      // Step 12: Publish
      case 12:
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Template title" data-testid="ugc2-title" />
            </div>
            <div>
              <Label className="text-sm">Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe this template..." data-testid="ugc2-description" />
            </div>
            <div>
              <Label className="text-sm">Recreation Credit Cost</Label>
              <p className="text-xs text-muted-foreground mb-1">Credits deducted per recreation. Leave blank for default.</p>
              <Input
                type="number"
                value={creditsCost}
                onChange={(e) => setCreditsCost(e.target.value)}
                placeholder="e.g., 3"
                className="w-40"
                min="0"
                step="0.5"
                data-testid="ugc2-credits-cost-input"
              />
            </div>
            <div>
              <Label className="text-sm">Tags</Label>
              <div className="flex gap-2 flex-wrap mt-1">
                {tags.map((tag, i) => (
                  <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setTags(tags.filter((_, j) => j !== i))}>{tag} x</Badge>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add tag"
                  className="flex-1"
                  onKeyDown={(e) => { if (e.key === "Enter" && newTag.trim()) { setTags([...tags, newTag.trim()]); setNewTag(""); } }}
                />
                <Button variant="outline" size="sm" onClick={() => { if (newTag.trim()) { setTags([...tags, newTag.trim()]); setNewTag(""); } }}>Add</Button>
              </div>
            </div>
            <Button
              onClick={publishTemplate}
              disabled={loading || !template?.sample_video || !title.trim() || !description.trim()}
              className="w-full" size="lg"
              data-testid="ugc2-publish-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Publish Template
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  // ─── Step progress indicator ───────────────────────────
  const getSceneLabel = (step) => {
    if (step.id <= 2) return null;
    if (step.id <= 6) return "Scene 1";
    if (step.id <= 10) return "Scene 2";
    return "Final";
  };

  return (
    <div className="space-y-6">
      {/* Step navigation */}
      <div className="flex gap-1.5 overflow-x-auto pb-2">
        {STEPS.map((step, idx) => {
          const sceneLabel = getSceneLabel(step);
          const isActive = currentStep === step.id;
          const isPast = currentStep > step.id;
          return (
            <Button
              key={step.id}
              variant={isActive ? "default" : isPast ? "secondary" : "outline"}
              size="sm"
              onClick={() => setCurrentStep(step.id)}
              className={`shrink-0 text-xs ${isPast ? "opacity-70" : ""}`}
              data-testid={`ugc2-step-${step.id}`}
            >
              {isPast && <Check className="w-3 h-3 mr-1" />}
              {step.title}
            </Button>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{STEPS[currentStep - 1]?.title}</CardTitle>
          <CardDescription>{STEPS[currentStep - 1]?.description}</CardDescription>
        </CardHeader>
        <CardContent>{renderStep()}</CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} disabled={currentStep === 1}>
          Previous
        </Button>
        <Button onClick={() => setCurrentStep(Math.min(STEPS.length, currentStep + 1))} disabled={currentStep === STEPS.length}>
          Next <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
